import type { TimeoffRequestRow } from '@routeback/core';
import { Empty } from '@routeback/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/app';
import {
  MONTHS, STATUS_LABEL, blackoutMonths, describeRequest, formatRange, holidays, monthGrid, monthOf, monthRange, todayIn, weekEnd, type OffEntry,
} from './timeoff';
import { useCalendarEvents, useMyTimeoff, useTeamOff } from './useTimeoffData';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Driver: the team time-off calendar (who's off, holidays, blackouts, my
 * requests) and my requests. Tap a day to see it and request from there.
 */
export function TimeOffView() {
  const app = useApp();
  const nav = useNavigate();
  const today = todayIn(app.details.timezone);
  const [ym, setYm] = useState(() => ({ y: +today.slice(0, 4), m: +today.slice(5, 7) }));
  const [picked, setPicked] = useState<string | null>(null);
  const { from, to } = monthRange(ym.y, ym.m);
  const { off, loaded } = useTeamOff(from, to);
  const mine = useMyTimeoff();
  const events = useCalendarEvents();
  const blackout = blackoutMonths(app.settings);
  const hol = useMemo(() => holidays(ym.y), [ym.y]);
  const grid = useMemo(() => monthGrid(ym.y, ym.m), [ym.y, ym.m]);

  const step = (n: number) => {
    setPicked(null);
    setYm(({ y, m }) => {
      const t = y * 12 + (m - 1) + n;
      return { y: Math.floor(t / 12), m: (t % 12) + 1 };
    });
  };

  const offOn = (d: string) => off.filter((o) => o.start_date <= d && o.end_date >= d);
  const mineOn = (d: string) => mine.filter((r) => r.span !== 'open' && (r.status === 'pending' || r.status === 'approved') && r.start_date! <= d && r.end_date! >= d);
  const eventsOn = (d: string) => events.filter((e) => e.start_date <= d && e.end_date >= d);
  const isBlackout = (d: string) => blackout.includes(monthOf(d)) || eventsOn(d).some((e) => e.kind === 'blackout');

  const current = mine.filter((r) => r.status === 'pending' || (r.status === 'approved' && (r.span === 'open' || (r.end_date ?? '') >= today)));
  const earlier = mine.filter((r) => !current.includes(r));
  const isThisMonth = ym.y === +today.slice(0, 4) && ym.m === +today.slice(5, 7);

  return (
    <div>
      <div className="settings-head"><Link to="/home" className="btn btn-sm btn-ghost">‹ Home</Link><h2>🏖 Time off</h2></div>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
          <button className="btn btn-sm btn-ghost" aria-label="Previous month" onClick={() => step(-1)}>‹</button>
          <strong style={{ fontSize: 17 }}>{MONTHS[ym.m - 1]} {ym.y}</strong>
          <button className="btn btn-sm btn-ghost" aria-label="Next month" onClick={() => step(1)}>›</button>
        </div>
        <div className="cal" role="grid" aria-label={`${MONTHS[ym.m - 1]} ${ym.y}`}>
          {DOW.map((d, i) => <div key={i} className="cal-dow" aria-hidden>{d}</div>)}
          {grid.flat().map((d, i) => {
            if (!d) return <div key={i} className="cal-cell cal-empty" />;
            const who = offOn(d);
            const my = mineOn(d);
            const h = hol[d];
            const ev = eventsOn(d).filter((e) => e.kind !== 'blackout');
            const cls = ['cal-cell'];
            if (isBlackout(d)) cls.push('cal-blackout');
            if (d === today) cls.push('cal-today');
            if (d < today) cls.push('cal-past');
            if (d === picked) cls.push('cal-picked');
            if (my.some((r) => r.status === 'approved')) cls.push('cal-mine-ok');
            else if (my.length) cls.push('cal-mine-pending');
            const others = who.filter((o) => o.user_id !== app.userId);
            return (
              <button key={i} type="button" className={cls.join(' ')} onClick={() => setPicked(d === picked ? null : d)}
                aria-label={`${formatRange(d, d)}${h ? `, ${h}` : ''}${others.length ? `, ${others.length} off` : ''}${my.length ? ', your request' : ''}`}>
                <span className="cal-num">{+d.slice(8)}</span>
                {(h || ev.length > 0) && <span className="cal-hol">{h ?? ev[0]!.title}</span>}
                {others.slice(0, 2).map((o) => <span key={o.request_id} className="cal-name">{o.first_name || 'Off'}</span>)}
                {others.length > 2 && <span className="cal-more">+{others.length - 2}</span>}
              </button>
            );
          })}
        </div>
        <div className="cal-legend muted small">
          <span><i className="cal-key cal-key-mine" /> Your request</span>
          <span><i className="cal-key cal-key-pending" /> Waiting</span>
          {blackout.length > 0 || events.some((e) => e.kind === 'blackout') ? <span><i className="cal-key cal-key-blackout" /> Blackout</span> : null}
          {!isThisMonth && <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setPicked(null); setYm({ y: +today.slice(0, 4), m: +today.slice(5, 7) }); }}>Today</button>}
        </div>
        {app.mode === 'cloud' && !loaded && <p className="muted small">Loading who's off…</p>}
        {picked && <DayPanel day={picked} today={today} off={offOn(picked).filter((o) => o.user_id !== app.userId)} mine={mineOn(picked)}
          holiday={hol[picked]} events={eventsOn(picked).map((e) => (e.kind === 'blackout' ? `Blackout: ${e.title}` : e.title))}
          blackoutMonth={blackout.includes(monthOf(picked))}
          onRequest={(span) => nav(`/timeoff/new?span=${span}&start=${picked}`)} />}
      </div>

      <div className="card row" style={{ justifyContent: 'space-between' }}>
        <div className="grow"><strong>Need time off?</strong><div className="muted small">Your manager approves it. You can change or cancel it until then.</div></div>
        <Link to="/timeoff/new" className="btn btn-primary">Request time off</Link>
      </div>

      <div className="section-title">My requests</div>
      {current.length ? <div className="list">{current.map((r) => <TimeoffItem key={r.id} r={r} />)}</div> : <div className="card"><Empty>No time off requested.</Empty></div>}
      {earlier.length > 0 && (<><div className="section-title">Earlier</div><div className="list">{earlier.slice(0, 20).map((r) => <TimeoffItem key={r.id} r={r} />)}</div></>)}
    </div>
  );
}

function DayPanel({ day, today, off, mine, holiday, events, blackoutMonth, onRequest }: {
  day: string; today: string; off: OffEntry[]; mine: TimeoffRequestRow[]; holiday?: string; events: string[]; blackoutMonth: boolean;
  onRequest: (span: 'days' | 'week') => void;
}) {
  const lines: string[] = [];
  if (holiday) lines.push(`🎉 ${holiday}`);
  for (const e of events) lines.push(`📌 ${e}`);
  if (blackoutMonth) lines.push(`⛔ ${MONTHS[monthOf(day) - 1]} is a blackout month — requests are reviewed case by case`);
  if (off.length) lines.push(`🏖 Off: ${off.map((o) => (o.first_name ? `${o.first_name} (${formatRange(o.start_date, o.end_date)})` : `someone (${formatRange(o.start_date, o.end_date)})`)).join(', ')}`);
  for (const r of mine) lines.push(`🙋 Your request: ${describeRequest(r)} — ${STATUS_LABEL[r.status].toLowerCase()}`);
  return (
    <div className="cal-day-panel">
      <strong>{formatRange(day, day)}</strong>
      {lines.length ? lines.map((l, i) => <div key={i} className="small" style={{ marginTop: 4 }}>{l}</div>) : <div className="muted small" style={{ marginTop: 4 }}>Nobody is off.</div>}
      {day >= today && (
        <div className="row row-wrap" style={{ gap: 8, marginTop: 10 }}>
          <button className="btn btn-sm" onClick={() => onRequest('days')}>Request this day</button>
          <button className="btn btn-sm" onClick={() => onRequest('week')}>Request {formatRange(day, weekEnd(day))}</button>
        </div>
      )}
    </div>
  );
}

export function TimeoffItem({ r }: { r: TimeoffRequestRow }) {
  return (
    <Link to={`/requests/timeoff/${r.id}`} className="list-item settings-link">
      <span style={{ width: 26, textAlign: 'center' }}>🏖</span>
      <div className="grow">
        <div>{r.span === 'open' ? 'Open request' : describeRequest(r)}</div>
        <div className="muted small">{r.span === 'open' ? `“${r.open_request.slice(0, 60)}${r.open_request.length > 60 ? '…' : ''}”` : r.note ? r.note.slice(0, 60) : 'Time off'}</div>
      </div>
      <span className={'chip' + (r.status === 'approved' ? ' on' : '')}>{STATUS_LABEL[r.status]}</span>
    </Link>
  );
}
